import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  Store,
  TrendingUp,
  Package,
  PhoneCall,
  ShieldCheck,
  FileText,
  BarChart3,
  Sparkles,
  Upload,
  Cpu,
  Smartphone,
} from "lucide-react";
import { tLang } from "@/contexts/PortalLanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CDN   = "https://pub-f22312f1902b4c5b98d85bf07b70d4e9.r2.dev";
const EMBER = "#FF6B35";
const TEAL  = "#00BFA5";
const NAVY  = "#1A1A2E";

export default function RetailersFR() {

  // ─── Section data ────────────────────────────────────────────────────────────

  const onboardingSteps = [
    {
      icon: Upload,
      title: tLang("fr", "retailers.onboarding.step1.title", "Téléversez votre catalogue produits"),
      desc:  tLang("fr", "retailers.onboarding.step1.desc",  "Ajoutez vos foyers, grils et produits extérieurs dans le portail Fyrris. Des photos de base et les spécifications suffisent pour commencer."),
    },
    {
      icon: Cpu,
      title: tLang("fr", "retailers.onboarding.step2.title", "Nous gérons le traitement RA"),
      desc:  tLang("fr", "retailers.onboarding.step2.desc",  "Notre équipe construit et optimise un modèle 3D photoréaliste pour chaque produit. Aucun travail technique requis de votre côté."),
    },
    {
      icon: Smartphone,
      title: tLang("fr", "retailers.onboarding.step3.title", "Les clients visualisent instantanément"),
      desc:  tLang("fr", "retailers.onboarding.step3.desc",  "Les acheteurs placent vos produits chez eux en RA — en magasin sur votre iPad ou chez eux sur leur propre appareil."),
    },
  ];

  const pricingTailored = tLang("fr", "retailers.pricing.tailored", "Tarification adaptée à votre magasin");
  const pricingOrderForm = tLang("fr", "retailers.pricing.orderFormLine", "Précisée dans votre bon de commande");

  const pricingPlans = [
    {
      key:      "founding",
      name:     tLang("fr", "retailers.pricing.founding.name",    "Détaillant fondateur"),
      badge:    tLang("fr", "retailers.pricing.founding.badge",   "Première cohorte"),
      subtext:  tLang("fr", "retailers.pricing.founding.subtext", "Une première cohorte limitée de détaillants"),
      features: [
        tLang("fr", "retailers.pricing.founding.f1", "Widget de visualisation RA (intégrable sur votre site)"),
        tLang("fr", "retailers.pricing.founding.f2", "Accès au catalogue fabricant"),
        tLang("fr", "retailers.pricing.founding.f3", "Portail d'administration de base"),
        tLang("fr", "retailers.pricing.founding.f4", "Support par courriel"),
        tLang("fr", "retailers.pricing.founding.f5", "Tarif garanti pour la durée de votre entente"),
        tLang("fr", "retailers.pricing.founding.f6", "Accès anticipé aux nouvelles fonctionnalités"),
        tLang("fr", "retailers.pricing.founding.f7", "Ligne directe avec le fondateur"),
      ],
      cta:      tLang("fr", "retailers.pricing.founding.cta", "Réserver une démo"),
      ctaHref:  "/fr/detaillants/demo",
      dotColor: EMBER,
      ctaColor: EMBER,
      badgeClass: "bg-orange-100 text-orange-700",
    },
    {
      key:      "standard",
      name:     tLang("fr", "retailers.pricing.standard.name",    "Standard"),
      badge:    tLang("fr", "retailers.pricing.standard.badge",   "Le plus populaire"),
      subtext:  "",
      features: [
        tLang("fr", "retailers.pricing.standard.f1", "Tout ce qui est inclus dans Fondateur"),
        tLang("fr", "retailers.pricing.standard.f2", "Catalogue fabricant complet"),
        tLang("fr", "retailers.pricing.standard.f3", "Analyses d'utilisation RA (lorsque disponible)"),
        tLang("fr", "retailers.pricing.standard.f4", "Données de prospects qualifiés"),
        tLang("fr", "retailers.pricing.standard.f5", "Support prioritaire"),
      ],
      cta:      tLang("fr", "retailers.pricing.standard.cta", "Réserver une démo"),
      ctaHref:  "/fr/detaillants/demo",
      dotColor: TEAL,
      ctaColor: TEAL,
      badgeClass: "bg-teal-100 text-teal-700",
    },
    {
      key:      "growth",
      name:     tLang("fr", "retailers.pricing.growth.name",    "Croissance"),
      badge:    tLang("fr", "retailers.pricing.growth.badge",   "Bientôt disponible"),
      subtext:  tLang("fr", "retailers.pricing.growth.subtext", "Prêt pour la marque blanche · multi-emplacements"),
      features: [
        tLang("fr", "retailers.pricing.growth.f1", "Tout ce qui est inclus dans Standard"),
        tLang("fr", "retailers.pricing.growth.f2", "Expérience marque blanche / personnalisée"),
        tLang("fr", "retailers.pricing.growth.f3", "Analyses avancées & rapports"),
        tLang("fr", "retailers.pricing.growth.f4", "Support multi-emplacements"),
        tLang("fr", "retailers.pricing.growth.f5", "Gestionnaire de compte dédié"),
      ],
      cta:      tLang("fr", "retailers.pricing.growth.cta", "Réserver une démo"),
      ctaHref:  "/fr/detaillants/demo",
      dotColor: "#9CA3AF",
      ctaColor: "#374151",
      badgeClass: "bg-gray-100 text-gray-500",
    },
  ];

  const faqItems = [
    {
      q: tLang("fr", "retailers.faq.q1", "Quels produits puis-je intégrer ?"),
      a: tLang("fr", "retailers.faq.a1", "Tout produit physique dans l'univers HPB — foyers, poêles à bois, inserts au gaz, grils BBQ, mobilier de terrasse, cuisines extérieures et plus. S'il a des dimensions, nous pouvons en construire un modèle RA."),
    },
    {
      q: tLang("fr", "retailers.faq.q2", "Combien de temps dure la mise en place ?"),
      a: tLang("fr", "retailers.faq.a2", "La plupart des détaillants sont en ligne en 2 à 3 semaines. Nous gérons tout le traitement 3D. Vous soumettez votre liste de produits et vos photos ; nous prenons le relais."),
    },
    {
      q: tLang("fr", "retailers.faq.q3", "Ai-je besoin d'une équipe technique ?"),
      a: tLang("fr", "retailers.faq.a3", "Non. Le portail Fyrris est conçu pour les utilisateurs non techniques. Si vous savez mettre à jour un tableur, vous pouvez gérer votre catalogue complet et vos emplacements."),
    },
    {
      q: tLang("fr", "retailers.faq.q4", "Que se passe-t-il une fois la cohorte fondatrice complète ?"),
      a: tLang("fr", "retailers.faq.a4", "La cohorte fondatrice est une première cohorte limitée. Les détaillants qui se joignent plus tard bénéficient aussi d'une tarification adaptée à leur magasin, précisée dans leur bon de commande. Tout engagement tarifaire est celui que prévoit votre bon de commande."),
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="bg-gray-950 text-gray-100">
      <Helmet>
        <title>{tLang("fr", "retailers.meta.title", "Fyrris pour les détaillants HPB")}</title>
        <meta name="description" content={tLang("fr", "retailers.meta.description")} />
        <meta name="keywords" content="détaillant HPB spécialisé, logiciel détaillant foyer, salle d'exposition mobilier terrasse, magasin gril BBQ, plateforme AR, détaillant vie extérieure" />
        <meta property="og:title" content={tLang("fr", "retailers.meta.ogTitle")} />
        <meta property="og:description" content={tLang("fr", "retailers.meta.ogDescription")} />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tLang("fr", "retailers.meta.ogTitle")} />
        <meta name="twitter:description" content={tLang("fr", "retailers.meta.ogDescription")} />
      </Helmet>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden pt-12 pb-20 md:pt-14 md:pb-24 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: NAVY }}
      >
        {/* Hero image */}
        <img
          src={`${CDN}/hero/Retail%20Page%20-%20Hero.png`}
          alt="Salle d'exposition d'un détaillant HPB avec foyers et produits extérieurs"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-[#1A1A2E]/70 pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto">
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold mb-6"
            style={{ backgroundColor: `${EMBER}22`, color: EMBER }}
          >
            <Store className="w-4 h-4" />
            {tLang("fr", "retailers.hero.eyebrow", "Pour les détaillants HPB")}
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white mb-6">
            {tLang("fr", "retailers.hero.headline1", "Convertissez l'intérêt en ventes en magasin")}
            <span className="block mt-2 text-3xl md:text-4xl lg:text-5xl font-semibold" style={{ color: EMBER }}>
              {tLang("fr", "retailers.hero.headline2", "grâce à l'AR qui répond à tous les doutes avant la visite.")}
            </span>
          </h1>

          <p className="text-gray-300 text-lg md:text-xl max-w-3xl mb-10 leading-relaxed">
            {tLang(
              "fr",
              "retailers.hero.subhead",
              "Fyrris aide vos clients à prévisualiser foyers et produits extérieurs chez eux — puis les guide vers votre magasin pour acheter. Moins de doutes, moins de retours, des achats plus confiants."
            )}
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/fr/detaillants/demo"
              className="inline-flex items-center gap-2 rounded-full px-8 py-3 font-medium text-white whitespace-nowrap transition hover:opacity-90"
              style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
            >
              {tLang("fr", "retailers.hero.cta", "Réserver une démo")}
              <Sparkles className="w-4 h-4" />
            </Link>
            <Link
              to="/fr/detaillants#pricing"
              className="inline-flex items-center gap-2 rounded-full px-8 py-3 font-medium text-gray-300 border border-gray-600 transition hover:border-gray-400 hover:text-white"
            >
              {tLang("fr", "retailers.hero.ctaSecondary", "Voir les tarifs")}
            </Link>
          </div>
        </div>
      </section>

      {/* ── BENEFITS GRID ────────────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center">
            Pourquoi les détaillants choisissent Fyrris
          </h2>

          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <TrendingUp className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Plus de trafic qualifié</h3>
              <p className="mt-2 text-gray-400 text-sm">
                Les clients explorent les produits chez eux, puis utilisent la géolocalisation
                pour trouver le magasin le plus proche qui les propose. Vous rencontrez des
                acheteurs qui savent déjà ce qu'ils veulent.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <Package className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Moins de retours</h3>
              <p className="mt-2 text-gray-400 text-sm">
                L'essai en RA fixe les bonnes attentes sur la taille, le style et la finition —
                moins de surprises après l'achat et des clients plus satisfaits.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <PhoneCall className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Conseils plus rapides</h3>
              <p className="mt-2 text-gray-400 text-sm">
                Les clients arrivent avec captures d'écran et mesures depuis l'app.
                Votre équipe recommande le bon modèle et conclut la vente plus vite.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <BarChart3 className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Insights actionnables</h3>
              <p className="mt-2 text-gray-400 text-sm">
                Voyez quels produits sont le plus prévisualisés dans votre région
                et adaptez vos stocks. Des métriques simples — sans équipe data.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <ShieldCheck className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Fiable & respectueux de la vie privée</h3>
              <p className="mt-2 text-gray-400 text-sm">
                Infrastructure moderne avec des paramètres de confidentialité stricts.
                Vos données restent les vôtres.
              </p>
            </div>

            <div className="rounded-2xl bg-gray-900 border border-gray-800 p-6">
              <Sparkles className="w-8 h-8" style={{ color: EMBER }} />
              <h3 className="mt-4 text-xl font-semibold">Mise en route simple</h3>
              <p className="mt-2 text-gray-400 text-sm">
                Ajoutez vos emplacements, téléversez votre logo et vous êtes en ligne.
                Notre équipe peut aider pour l'intégration produit si nécessaire.
              </p>
            </div>
          </div>

          {/* Benefits CTA */}
          <div className="mt-12 flex flex-col items-center gap-3">
            <Link
              to="/fr/detaillants/demo"
              className="inline-flex shrink-0 items-center gap-2 rounded-full px-8 py-3 font-medium text-white whitespace-nowrap transition hover:opacity-90"
              style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
            >
              {tLang("fr", "retailers.pricing.founding.cta", "Réserver une démo")}
              <Sparkles className="w-4 h-4 shrink-0" />
            </Link>
            <p className="text-sm text-gray-400">
              Déjà partenaire ?{" "}
              <Link to="/signin" className="hover:underline" style={{ color: TEAL }}>
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL ──────────────────────────────────────────────────────── */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl bg-gray-900 border border-gray-800 px-8 py-10 md:px-12 md:py-12 relative overflow-hidden">
            <span
              className="absolute top-4 left-6 text-7xl font-serif leading-none select-none pointer-events-none"
              aria-hidden="true"
              style={{ color: EMBER, opacity: 0.25 }}
            >
              &ldquo;
            </span>

            <blockquote className="relative space-y-5">
              <p className="text-lg md:text-xl text-gray-200 leading-relaxed font-medium">
                {tLang(
                  "fr",
                  "retailers.testimonial.quote",
                  "Fyrris a changé la façon dont nos clients magasinent les foyers. Ils arrivent en sachant exactement ce qu'ils veulent."
                )}
              </p>
              <footer className="text-sm text-gray-500">
                — {tLang("fr", "retailers.testimonial.attribution", "[Nom du détaillant], Spécialiste HPB")}
              </footer>
            </blockquote>

            <div
              className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-10 pointer-events-none"
              style={{ backgroundColor: EMBER }}
            />
          </div>
        </div>
      </section>

      {/* ── HOW ONBOARDING WORKS ─────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: NAVY }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {tLang("fr", "retailers.onboarding.title", "Comment fonctionne l'intégration")}
          </h2>
          <p className="text-center text-gray-400 mb-14 max-w-xl mx-auto">
            {tLang("fr", "retailers.onboarding.subtitle", "Vous êtes en ligne en moins de trois semaines. Aucun développeur requis.")}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {onboardingSteps.map(({ icon: Icon, title, desc }, idx) => (
              <div key={title} className="relative flex flex-col gap-5 bg-gray-900 rounded-xl p-6">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                  style={{ backgroundColor: EMBER }}
                >
                  {idx + 1}
                </div>

                <Icon className="w-7 h-7" style={{ color: TEAL }} />

                <h3 className="text-xl font-semibold text-white">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>

                {idx < onboardingSteps.length - 1 && (
                  <div
                    className="hidden md:block absolute top-7 left-full w-8 h-px"
                    style={{ backgroundColor: `${EMBER}44` }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {tLang("fr", "retailers.pricing.title", "Une tarification adaptée à votre magasin")}
          </h2>
          <p className="text-center text-gray-400 mb-14 max-w-xl mx-auto">
            {tLang("fr", "retailers.pricing.subtitle", "Précisée dans votre bon de commande, sans surprise.")}
          </p>

          <div className="grid md:grid-cols-3 gap-6 items-start">
            {pricingPlans.map((plan) => (
              <div
                key={plan.key}
                className={`relative rounded-2xl flex flex-col bg-white shadow-xl overflow-hidden ${
                  plan.key === "founding" ? "md:scale-[1.02] md:z-10" : ""
                }`}
                style={
                  plan.key === "founding"
                    ? { border: `2px solid ${EMBER}` }
                    : { border: "1px solid #E5E7EB" }
                }
              >
                {/* 1. Badge pill */}
                <div className="px-6 pt-6">
                  <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${plan.badgeClass}`}>
                    {plan.badge}
                  </span>
                </div>

                {/* 2. Plan name */}
                <div className="px-6 pt-3">
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                </div>

                {/* 3. Pricing line: no public prices. Tailored to the store, set out in the Order Form. */}
                <div className="px-6 pt-2">
                  <p className="text-lg font-semibold text-gray-900">{pricingTailored}</p>
                  <p className="text-sm text-gray-500">{pricingOrderForm}</p>
                </div>

                {/* 4. Subtext */}
                {plan.subtext && <p className="px-6 pt-1 text-sm text-gray-500">{plan.subtext}</p>}

                {/* 5. Divider */}
                <div className="mx-6 mt-5 border-t border-gray-100" />

                {/* 6. Feature list */}
                <ul className="px-6 pt-5 flex flex-col gap-3 flex-1">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span
                        className="mt-1.5 h-2 w-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: plan.dotColor }}
                      />
                      {feat}
                    </li>
                  ))}
                </ul>

                {/* 8. Divider */}
                <div className="mx-6 mt-4 border-t border-gray-100" />

                {/* 10. CTA */}
                <div className="px-6 pt-4 pb-6">
                  <Link
                    to={plan.ctaHref}
                    className="w-full inline-flex items-center justify-center rounded-full py-3 text-sm font-semibold text-white whitespace-nowrap transition hover:opacity-90"
                    style={{ backgroundColor: plan.ctaColor }}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Pricing note */}
          <div className="mt-10 mx-auto max-w-md bg-gray-900 rounded-xl p-4 flex items-center gap-3">
            <FileText className="w-5 h-5 flex-shrink-0" style={{ color: TEAL }} />
            <p className="text-sm text-gray-400">
              {tLang(
                "fr",
                "retailers.pricing.orderFormNote",
                "Votre tarification est adaptée à votre magasin et précisée dans votre bon de commande."
              )}
            </p>
          </div>

        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────────── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: NAVY }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {tLang("fr", "retailers.faq.title", "Questions fréquentes")}
          </h2>
          <p className="text-center text-gray-400 mb-12">
            {tLang("fr", "retailers.faq.subtitle", "D'autres questions ? Écrivez-nous à support@fyrris.com")}
          </p>

          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, idx) => (
              <AccordionItem
                key={idx}
                value={`item-${idx}`}
                className="border-gray-800"
              >
                <AccordionTrigger className="text-white hover:text-white text-base font-semibold py-5 text-left">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 pb-5 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 text-center" style={{ paddingBottom: "0.5rem" }}>
            <Link
              to={"/fr/detaillants/demo"}
              className="transition hover:opacity-90"
              style={{
                backgroundColor: "#FF6B35",
                color: "white",
                borderRadius: "9999px",
                padding: "0.75rem 2.5rem",
                whiteSpace: "nowrap",
                fontWeight: "500",
                width: "auto",
                display: "inline-block",
                boxShadow: "0 4px 14px rgba(255,107,53,0.3)",
              }}
            >
              {tLang("fr", "retailers.faq.cta", "Réserver une démo →")}
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
